import {
  ChangeDetectorRef,
  Component,
  Injectable,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  inject,
  Inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { MatDividerModule } from '@angular/material/divider';
import { NgSelectModule } from '@ng-select/ng-select';
import { RxIconModule } from '@nrwl/rx-icon';
import { TokenToDivPipe } from '../../../helpers/token-to-div.pipe';
import { WidgetBase } from '../../../models/widget-base';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  pairwise,
  switchMap,
  startWith,
  take,
  takeUntil,
} from 'rxjs/operators';
import { environment } from '@nrwl/environment';
import { FbEmrEquipmentWidget } from '../../../models/form-widget-emr-equipment.model';
import { EmrReadonlyNotice } from '../../../helpers/components/emr-readonly-notice';
import { TextareaAutosizeDirective } from '../shared/textarea-autosize-directive';
import { PasteStripDirective } from '../shared/paste-strip-directive';
import { RX_APP_CONTEXT, RxAppContext } from '@nrwl/rx-root-state';
import { ScribeSuggestionIndicatorComponent } from '../shared/scribe-suggestion-indicator/scribe-suggestion-indicator.component';
import { AmendmentFieldIndicatorComponent } from '../shared/amendment-field-indicator/amendment-field-indicator.component';
import { ScribeSuggestionMarkerTarget } from '../../scribe-suggestion-marker.model';
import { copyScribeSuggestionMarker } from '../../scribe-suggestion-marker.util';

export interface EquipmentItem {
  id?: string | number;
  equipmentType?: string;
  time?: unknown;
  quantity?: unknown;
  unit?: unknown;
  notes?: string;
  attribute1?: string;
  attribute2?: string;
  attribute3?: string;
  attribute4?: string;
  attribute5?: string;
  attribute6?: string;
  attribute7?: string;
  attribute8?: string;
  attribute9?: string;
  attribute10?: string;
}

export interface GetEquipmentParams {
  readonly customerId: string;
  readonly treatmentId: string;
}

export interface GetTreatmentParams {
  readonly appointmentId: string;
}

type TreatmentResponse = { id?: string } | null;

@Injectable({ providedIn: 'root' })
export class EquipmentService {
  private readonly http = inject(HttpClient);

  public getEquipment(params: GetEquipmentParams): Observable<EquipmentItem[]> {
    const queryParams: Record<string, string> = {
      CustomerId: params.customerId,
      TreatmentId: params.treatmentId,
      legacyids: 'true',
    };

    return this.http
      .get<unknown>(`${environment.baseAPIUrlMicro}egress-proxy/pne/treatmentequipmentassign`, {
        params: queryParams,
      })
      .pipe(map((response) => this.parseEquipmentResponse(response)));
  }

  public getTreatment(params: GetTreatmentParams): Observable<TreatmentResponse> {
    const queryParams = { legacyids: 'true' };

    return this.http
      .get<unknown>(
        `${environment.baseAPIUrlMicro}egress-proxy/pne/appointments/${params.appointmentId}/treatment/`,
        {
          params: queryParams,
        },
      )
      .pipe(map((response) => this.parseTreatmentResponse(response)));
  }

  private parseEquipmentResponse(response: unknown): EquipmentItem[] {
    const data =
      (Array.isArray(response) && response) ||
      (response as any)?.Data ||
      (response as any)?.data ||
      response;

    if (Array.isArray(data)) {
      return data.map((item) => this.normalizeItem(item)).filter(Boolean);
    }

    if (data && typeof data === 'object') {
      const items =
        (data as any)?.Items ||
        (data as any)?.items ||
        (data as any)?.Equipment ||
        (data as any)?.equipment;

      if (Array.isArray(items)) {
        return items.map((item) => this.normalizeItem(item)).filter(Boolean);
      }

      return [this.normalizeItem(data)].filter(Boolean);
    }

    return [];
  }

  private parseTreatmentResponse(response: unknown): TreatmentResponse {
    if (response && typeof response === 'object' && !Array.isArray(response)) {
      const record = response as Record<string, unknown>;
      const id = record['treatmentId'] ?? record['TreatmentId'] ?? record['Id'] ?? record['id'];
      if (id !== undefined && id !== null && String(id).trim() !== '') {
        return { id: String(id).trim() };
      }
    }

    if (Array.isArray(response) && response.length > 0) {
      const item = response[0];
      if (item && typeof item === 'object') {
        const id =
          (item as any)?.Id ??
          (item as any)?.id ??
          (item as any)?.treatmentId ??
          (item as any)?.TreatmentId;
        return id ? { id: String(id).trim() } : null;
      }
    }

    return null;
  }

  private normalizeItem(item: any): EquipmentItem | null {
    if (!item || typeof item !== 'object') {
      return null;
    }

    const attributes =
      (Array.isArray(item.Attributes) && item.Attributes) ||
      (Array.isArray(item.attributes) && item.attributes) ||
      [];

    const attributeFallbacks = Array.from({ length: 10 }, (_, index) => {
      const pos = index + 1;
      return (
        item[`AttributeId${pos}`] ??
        item[`Attribute${pos}`] ??
        item[`attribute${pos}`] ??
        attributes[index] ??
        ''
      );
    });

    return {
      id: item.Id ?? item.id ?? null,
      equipmentType: item.TreatmentEquipmentId ?? item.EquipmentType ?? item.equipmentType ?? '',
      time: item.TimeMins ?? item.timeMins ?? item.Time ?? item.time ?? '',
      quantity: item.Qty ?? item.qty ?? item.Quantity ?? item.quantity ?? '',
      unit: item.Unit ?? item.unit ?? '',
      notes: item.Note ?? item.note ?? item.Notes ?? item.notes ?? '',
      attribute1: attributeFallbacks[0],
      attribute2: attributeFallbacks[1],
      attribute3: attributeFallbacks[2],
      attribute4: attributeFallbacks[3],
      attribute5: attributeFallbacks[4],
      attribute6: attributeFallbacks[5],
      attribute7: attributeFallbacks[6],
      attribute8: attributeFallbacks[7],
      attribute9: attributeFallbacks[8],
      attribute10: attributeFallbacks[9],
    };
  }
}

type EquipmentFormItem = {
  id: string | null;
  equipmentType: string;
  equipmentTypeLabel?: string | null;
  time: string;
  quantity: string;
  unit: string;
  notes: string;
  attribute1: string;
  attribute1Label?: string | null;
  attribute2: string;
  attribute2Label?: string | null;
  attribute3: string;
  attribute3Label?: string | null;
  attribute4: string;
  attribute4Label?: string | null;
  attribute5: string;
  attribute5Label?: string | null;
  attribute6: string;
  attribute6Label?: string | null;
  attribute7: string;
  attribute7Label?: string | null;
  attribute8: string;
  attribute8Label?: string | null;
  attribute9: string;
  attribute9Label?: string | null;
  attribute10: string;
  attribute10Label?: string | null;
};

type EquipmentSuggestionPatch = Partial<
  Pick<
    EquipmentFormItem,
    | 'equipmentType'
    | 'time'
    | 'quantity'
    | 'unit'
    | 'notes'
    | 'attribute1'
    | 'attribute2'
    | 'attribute3'
    | 'attribute4'
    | 'attribute5'
    | 'attribute6'
    | 'attribute7'
    | 'attribute8'
    | 'attribute9'
    | 'attribute10'
  >
>;

@Component({
  selector: 'fb-emr-equipment',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDividerModule,
    NgSelectModule,
    RxIconModule,
    TokenToDivPipe,
    EmrReadonlyNotice,
    TextareaAutosizeDirective,
    PasteStripDirective,
    ScribeSuggestionIndicatorComponent,
    AmendmentFieldIndicatorComponent,
  ],
  templateUrl: './fb-emr-equipment.component.html',
  styleUrls: ['./fb-emr-equipment.component.scss'],
})
export class FbEmrEquipmentComponent extends WidgetBase implements OnInit, OnDestroy, OnChanges {
  @Input() public isTemplatePrint = false;

  @Input()
  public override set isEmrReadonly(value: boolean) {
    super.isEmrReadonly = value;

    this.applyReadOnlyState();

    this.equipmentControls.forEach((group) => {
      this.updateAttributeControlsState(group);
    });

    this.cd.markForCheck();
  }
  public override get isEmrReadonly() {
    return super.isEmrReadonly;
  }

  equipmentTypes$ = new BehaviorSubject<Array<{ text: string; value: string }>>([]);
  isLoadingEquipment = false;
  readonly attributeIndexes = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
  public isWidgetHidden = false;

  private equipmentLoading = false;
  private equipmentLoaded = false;
  private _treatmentId: string | number | null = null;

  equipmentForm: FormGroup;

  private searchTerm$ = new BehaviorSubject<string>('');
  private currentPage = 1;
  private pageSize = 30;
  private hasMoreEquipment = true;
  private allEquipmentTypes: Array<{ text: string; value: string }> = [];
  private equipmentAttributeIndexesByType = new Map<string, number[]>();
  private attributeSearchTerm$ = new BehaviorSubject<string>('');
  private attributeCurrentPage = 1;
  private attributePageSize = 100;
  private hasMoreAttributes = true;
  private attributeLastSearchTerm = '';
  private allAttributeOptions: Array<{
    text: string;
    value: string;
    index: number | null;
    equipmentTypeId: string | null;
  }> = [];
  isLoadingAttributes = false;
  private destroy$ = new Subject<void>();
  private equipmentGroupSubscriptions = new Map<FormGroup, Subject<void>>();
  private suppressPayloadUpdate = false;
  attributeOptionsByEquipmentType = new Map<
    string,
    {
      generic: Array<{ text: string; value: string }>;
      byIndex: { [key: number]: Array<{ text: string; value: string }> };
    }
  >();
  attributeOptionsByIndex: { [key: number]: Array<{ text: string; value: string }> } = {};
  attributeOptionsGeneric: Array<{ text: string; value: string }> = [];

  constructor(
    injector: Injector,
    private cd: ChangeDetectorRef,
    private http: HttpClient,
    private fb: FormBuilder,
    private equipmentService: EquipmentService,
    @Inject(RX_APP_CONTEXT)
    private appContext: RxAppContext,
  ) {
    super(injector);
    this.equipmentForm = this.fb.group({
      items: this.fb.array([]),
    });
  }

  get equipmentItems(): FormArray {
    return this.equipmentForm.get('items') as FormArray;
  }

  get equipmentControls(): FormGroup[] {
    return this.equipmentItems.controls as FormGroup[];
  }

  private get isRxStaffContext(): boolean {
    return this.appContext === RxAppContext.RxStaff;
  }

  /**
   * True while a patient's own plan is being customized. The widget then draws one empty
   * equipment entry and never reads or writes the saved equipment, so it is stored back unchanged.
   */
  public get isInstanceDesign(): boolean {
    return !!this.isDesign && !!this.entity;
  }

  ngOnInit() {
    if (!this.isPrinting && !this.isEmrReadonly) {
      this.loadEquipmentTypes();
      this.loadAttributeOptions();
    }
    this.initializeData();
    this.tryLoadEquipment();
    this.applyReadOnlyState();

    this.searchTerm$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((searchTerm) => {
        this.currentPage = 1;
        this.loadEquipmentTypes(searchTerm);
      });

    this.attributeSearchTerm$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((searchTerm) => {
        this.attributeCurrentPage = 1;
        this.loadAttributeOptions(searchTerm);
      });

    this.equipmentForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.suppressPayloadUpdate) {
        return;
      }
      this.updatePayload();
      this.cd.markForCheck();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isDesign'] || changes['isPrinting']) {
      this.applyReadOnlyState();
      this.equipmentControls.forEach((group) => {
        this.updateAttributeControlsState(group);
      });
      this.cd.markForCheck();
    }
  }

  private applyReadOnlyState(): void {
    const isReadOnly = this.isDesign || this.isPrinting || this.isEmrReadonly;
    if (isReadOnly) {
      this.equipmentForm.disable({ emitEvent: false });
    } else {
      this.equipmentForm.enable({ emitEvent: false });
    }
  }

  override ngOnDestroy() {
    this.equipmentGroupSubscriptions.forEach((teardown$) => {
      teardown$.next();
      teardown$.complete();
    });
    this.equipmentGroupSubscriptions.clear();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadEquipmentTypes(searchTerm = '') {
    if (this.isPrinting || this.isEmrReadonly || this.isRxStaffContext) return;
    if (this.isLoadingEquipment) return;

    this.isLoadingEquipment = true;

    const params = new URLSearchParams();
    params.append('Page', this.currentPage.toString());
    params.append('Rows', this.pageSize.toString());
    if (searchTerm) {
      params.append('Sort', searchTerm);
    }

    const url = `${environment.baseAPIUrlMicro}egress-proxy/pne/treatmentequipmentitems?${params.toString()}`;

    this.http
      .get<any>(url, { observe: 'response' })
      .pipe(
        map((response) => {
          const data = response.body?.Data || response.body || [];
          const equipmentTypes = (Array.isArray(data) ? data : [])
            .map((item: any) => {
              const value = item.Id ?? item.EquipmentId ?? item.Code;
              if (value === null || value === undefined || value === '') {
                return null;
              }
              this.updateEquipmentAttributeConfig(item, value);
              return {
                text: item.Name || item.EquipmentName || item.Description || '',
                value: String(value),
              };
            })
            .filter(Boolean);
          const totalPages = this.getTotalPages(
            response.headers,
            this.pageSize,
            equipmentTypes.length,
          );
          return { equipmentTypes, totalPages };
        }),
        catchError(() => {
          return of({ equipmentTypes: [], totalPages: null });
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(({ equipmentTypes, totalPages }) => {
        if (this.currentPage === 1) {
          const selectedValues = this.getSelectedEquipmentTypeValues();
          const selectedOptions = this.allEquipmentTypes.filter((option) =>
            selectedValues.has(String(option.value)),
          );
          const merged = this.mergeEquipmentOptions([...selectedOptions, ...equipmentTypes]);
          this.allEquipmentTypes = merged;
          this.equipmentTypes$.next(merged);
        } else {
          this.allEquipmentTypes = [...this.allEquipmentTypes, ...equipmentTypes];
          this.equipmentTypes$.next(this.allEquipmentTypes);
        }

        this.hasMoreEquipment =
          totalPages === null
            ? equipmentTypes.length >= this.pageSize
            : totalPages > 0 && this.currentPage < totalPages;
        this.isLoadingEquipment = false;
        this.cd.markForCheck();
      });
  }

  private loadAttributeOptions(searchTerm = '') {
    if (this.isPrinting || this.isEmrReadonly || this.isRxStaffContext) return;
    if (this.isLoadingAttributes) return;

    this.attributeLastSearchTerm = searchTerm;
    this.isLoadingAttributes = true;

    const params = new URLSearchParams();
    params.append('Page', this.attributeCurrentPage.toString());
    params.append('Rows', this.attributePageSize.toString());
    if (searchTerm) {
      params.append('Sort', searchTerm);
    }

    const url = `${environment.baseAPIUrlMicro}egress-proxy/pne/treatmentequipmentattributes?${params.toString()}`;

    this.http
      .get<unknown>(url, { observe: 'response' })
      .pipe(
        map((response) => {
          const data = (response as any)?.body?.Data || (response as any)?.body || [];
          const options = Array.isArray(data)
            ? (data.map((item) => this.parseAttributeOption(item)).filter(Boolean) as Array<{
                text: string;
                value: string;
                index: number | null;
                equipmentTypeId: string | null;
              }>)
            : [];
          const totalPages = this.getTotalPages(
            (response as any)?.headers,
            this.attributePageSize,
            options.length,
          );
          return { options, totalPages };
        }),
        catchError(() => {
          return of({ options: [], totalPages: null });
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(({ options, totalPages }) => {
        if (this.attributeCurrentPage === 1) {
          this.allAttributeOptions = options;
        } else {
          this.allAttributeOptions = [...this.allAttributeOptions, ...options];
        }

        const genericFromResponse: Array<{ text: string; value: string }> = [];
        const byIndexFromResponse: { [key: number]: Array<{ text: string; value: string }> } = {};
        const byTypeFromResponse = new Map<
          string,
          {
            generic: Array<{ text: string; value: string }>;
            byIndex: { [key: number]: Array<{ text: string; value: string }> };
          }
        >();

        this.allAttributeOptions.forEach((option) => {
          const entry = { text: option.text, value: option.value };
          if (option.equipmentTypeId) {
            const typeId = option.equipmentTypeId;
            if (!byTypeFromResponse.has(typeId)) {
              byTypeFromResponse.set(typeId, { generic: [], byIndex: {} });
            }
            const store = byTypeFromResponse.get(typeId);
            if (option.index && option.index >= 1 && option.index <= 10) {
              if (!store.byIndex[option.index]) {
                store.byIndex[option.index] = [];
              }
              store.byIndex[option.index].push(entry);
            } else {
              store.generic.push(entry);
            }
            return;
          }
          if (option.index && option.index >= 1 && option.index <= 10) {
            if (!byIndexFromResponse[option.index]) {
              byIndexFromResponse[option.index] = [];
            }
            byIndexFromResponse[option.index].push(entry);
          } else {
            genericFromResponse.push(entry);
          }
        });

        this.attributeOptionsGeneric = this.mergeAttributeOptions([
          ...this.attributeOptionsGeneric,
          ...genericFromResponse,
        ]);

        this.attributeIndexes.forEach((index) => {
          const existing = this.attributeOptionsByIndex[index] || [];
          const incoming = byIndexFromResponse[index] || [];
          this.attributeOptionsByIndex[index] = this.mergeAttributeOptions([
            ...existing,
            ...incoming,
          ]);
        });

        byTypeFromResponse.forEach((store, typeId) => {
          const existing = this.attributeOptionsByEquipmentType.get(typeId) || {
            generic: [],
            byIndex: {},
          };
          const mergedGeneric = this.mergeAttributeOptions([...existing.generic, ...store.generic]);
          const mergedByIndex: { [key: number]: Array<{ text: string; value: string }> } = {
            ...existing.byIndex,
          };
          this.attributeIndexes.forEach((index) => {
            const existingIndexOptions = existing.byIndex[index] || [];
            const incomingIndexOptions = store.byIndex[index] || [];
            if (incomingIndexOptions.length === 0 && existingIndexOptions.length === 0) {
              return;
            }
            mergedByIndex[index] = this.mergeAttributeOptions([
              ...existingIndexOptions,
              ...incomingIndexOptions,
            ]);
          });
          this.attributeOptionsByEquipmentType.set(typeId, {
            generic: mergedGeneric,
            byIndex: mergedByIndex,
          });
        });

        this.updateAttributeIndexesByType();

        this.hasMoreAttributes =
          totalPages === null
            ? options.length >= this.attributePageSize
            : totalPages > 0 && this.attributeCurrentPage < totalPages;
        this.isLoadingAttributes = false;
        this.cd.markForCheck();

        if (this.attributeLastSearchTerm === '' && totalPages !== null) {
          if (totalPages > 0 && this.attributeCurrentPage < totalPages) {
            this.attributeCurrentPage++;
            this.loadAttributeOptions('');
          }
        }
      });
  }

  customSearchFn = (term: string, item: { text?: string; value?: string }) => {
    const query = (term || '').toLowerCase();
    if (!query) {
      return true;
    }

    const text = (item?.text || '').toLowerCase();
    const value = (item?.value || '').toLowerCase();
    return text.includes(query) || value.includes(query);
  };

  onSearch(searchTerm: { term: string }) {
    this.searchTerm$.next(searchTerm.term);
  }

  onScrollToEnd() {
    if (!this.hasMoreEquipment || this.isLoadingEquipment) return;

    this.currentPage++;
    this.loadEquipmentTypes(this.searchTerm$.value);
  }

  onDropdownOpen() {
    if (this.isEmrReadonly) {
      return;
    }

    if (this.allEquipmentTypes.length === 0) {
      this.currentPage = 1;
      this.loadEquipmentTypes();
    }
  }

  onAttributeSearch(searchTerm: { term: string }) {
    this.attributeSearchTerm$.next(searchTerm.term);
  }

  onAttributeScrollToEnd() {
    if (!this.hasMoreAttributes || this.isLoadingAttributes) return;

    this.attributeCurrentPage++;
    this.loadAttributeOptions(this.attributeSearchTerm$.value);
  }

  onAttributeDropdownOpen() {
    if (this.isEmrReadonly) {
      return;
    }

    if (this.allAttributeOptions.length === 0) {
      this.attributeCurrentPage = 1;
      this.loadAttributeOptions();
    }
  }

  getAttributeControlName(index: number): string {
    return `attribute${index}`;
  }

  @Input()
  public override set payload(value) {
    this._payload = value;
    this.initializeData();
    this.ready.next(true);
  }
  public override get payload() {
    return this._payload;
  }

  @Input()
  public override set patientId(value) {
    this._patientId = value;
    this.tryLoadEquipment();
  }
  public override get patientId() {
    return this._patientId;
  }

  @Input()
  public override set patient(value) {
    this._patient = value;
    this.tryLoadEquipment();
  }
  public override get patient() {
    return this._patient;
  }

  @Input()
  public override set entity(value) {
    this._entity = value;
    this.tryLoadEquipment();
  }
  public override get entity() {
    return this._entity;
  }

  @Input()
  public override set isPreview(value: boolean) {
    super.isPreview = value;
    this.initializeData();
    this.tryLoadEquipment();
  }
  public override get isPreview() {
    return super.isPreview;
  }

  @Input()
  public override set isDesign(value: boolean) {
    super.isDesign = value;
    this.initializeData();
    this.tryLoadEquipment();
  }
  public override get isDesign() {
    return super.isDesign;
  }

  @Input()
  public override set appointment(value) {
    this._appointment = value;
    this.tryLoadEquipment();
  }
  public override get appointment() {
    return this._appointment;
  }

  @Input()
  public set treatmentId(value) {
    this._treatmentId = value;
    this.tryLoadEquipment();
  }
  public get treatmentId() {
    return this._treatmentId;
  }

  private tryLoadEquipment() {
    if (this.equipmentLoaded || this.equipmentLoading) {
      return;
    }

    if (this.isRxStaffContext) {
      return;
    }

    if (this.isPrinting || this.isEmrReadonly) {
      return;
    }

    if (this.isDesign) {
      return;
    }

    const isTemplate = this.isTemplateEntity();
    if (isTemplate && this.isPreview) {
      return;
    }

    const hasEquipment = this.hasExistingEquipment() || this.hasFormEquipmentValues();

    const shouldFetchTemplateFillAndSign = isTemplate && !this.isPreview && !this.isPrinting;
    if (!shouldFetchTemplateFillAndSign && hasEquipment) {
      return;
    }

    const patientId = this.patient?.remote_id;
    const treatmentId = this.appointment?.TreatmentId ?? this.entity?.metadata?.treatment_id;
    const appointmentId =
      this.appointment?.AppointmentId ??
      this.entity?.metadata?.appointment_id ??
      this.appointmentId;

    if (!patientId || (!treatmentId && !appointmentId)) {
      return;
    }

    this.equipmentLoading = true;

    const customerId = String(patientId);
    const loadEquipment = (resolvedTreatmentId: string) =>
      this.equipmentService.getEquipment({
        customerId,
        treatmentId: resolvedTreatmentId,
      });

    const equipment$ = treatmentId
      ? loadEquipment(String(treatmentId))
      : this.equipmentService
          .getTreatment({
            appointmentId: String(appointmentId),
          })
          .pipe(
            map((treatment) => treatment?.id),
            switchMap((resolved) => (resolved ? loadEquipment(resolved) : of([]))),
          );

    equipment$
      .pipe(
        take(1),
        catchError(() => {
          return of([]);
        }),
      )
      .subscribe((items) => {
        this.equipmentLoading = false;
        this.equipmentLoaded = true;

        if (!items.length) {
          return;
        }

        const mapped = items.map((item) => this.normalizeEquipment(item));
        this.setFormItems(mapped);
        this.updatePayload();
        this.cd.markForCheck();
      });
  }

  private initializeData() {
    if (!this.payload?.properties) return;

    if (this.isInstanceDesign) {
      // Draw the default look, even when the saved widget is hidden or holds several entries.
      this.isWidgetHidden = false;
      this.setFormItems([]);
      return;
    }

    const isTemplate = this.isTemplateEntity();
    const isTemplateReadOnly = isTemplate && (this.isPreview || this.isDesign);
    const properties = this.payload.properties;
    const isHidden = !!properties.items?.hidden;
    if (isHidden) {
      this.isWidgetHidden = true;
      return;
    }
    const itemsValue = properties.items?.value;
    const hasFormValues = this.hasFormEquipmentValues();
    const hasIncomingItems = Array.isArray(itemsValue) && itemsValue.length > 0;
    const metadataItems = this.getMetadataEquipments();
    const hasMetadataItems = metadataItems.length > 0;

    if (isTemplateReadOnly) {
      this.setFormItems([]);
      return;
    } else if (hasIncomingItems) {
      const mapped = itemsValue.map((equipment) => this.normalizeEquipment(equipment));
      const hydrated = hasMetadataItems
        ? this.mergeEquipmentWithMetadata(mapped, metadataItems)
        : mapped;
      this.setFormItems(hydrated);
    } else if (!hasFormValues && this.equipmentItems.length === 0) {
      if (hasMetadataItems) {
        const mapped = metadataItems.map((equipment) =>
          this.normalizeEquipmentFromMetadata(equipment),
        );
        this.setFormItems(mapped);
      } else {
        this.setFormItems([]);
      }
    }

    if (!properties.items) {
      properties.items = { value: [] };
    }

    if (!isTemplateReadOnly && (hasIncomingItems || hasFormValues || hasMetadataItems)) {
      this.updatePayload();
    }

    if (this.isEmrReadonly) {
      properties.items.formInvalid = false;
    }
  }

  private hasExistingEquipment(): boolean {
    const items = this.payload?.properties?.items?.value;

    if (!Array.isArray(items)) {
      return false;
    }

    return items.some((equipment) => this.hasEquipmentValue(equipment));
  }

  private mergeEquipmentWithMetadata(
    items: EquipmentFormItem[],
    metadataItems: any[],
  ): EquipmentFormItem[] {
    if (!Array.isArray(metadataItems) || metadataItems.length === 0) {
      return items;
    }

    return items.map((item, index) => {
      if (item.id) {
        return item;
      }

      const metadata = metadataItems[index];
      if (!metadata || typeof metadata !== 'object') {
        return item;
      }

      const id = this.normalizeIdValue(
        metadata?.id ?? metadata?.Id ?? metadata?.equipment_id ?? metadata?.equipmentId ?? null,
      );

      return id ? { ...item, id } : item;
    });
  }

  private hasEquipmentValue(equipment: any): boolean {
    if (!equipment || typeof equipment !== 'object') {
      return false;
    }

    const attributeKeys = [
      'attribute1',
      'attribute2',
      'attribute3',
      'attribute4',
      'attribute5',
      'attribute6',
      'attribute7',
      'attribute8',
      'attribute9',
      'attribute10',
      'Attribute1',
      'Attribute2',
      'Attribute3',
      'Attribute4',
      'Attribute5',
      'Attribute6',
      'Attribute7',
      'Attribute8',
      'Attribute9',
      'Attribute10',
    ];

    const values = [
      equipment.equipmentType,
      equipment.EquipmentType,
      equipment.time,
      equipment.Time,
      equipment.quantity,
      equipment.Quantity,
      equipment.unit,
      equipment.Unit,
      equipment.notes,
      equipment.Notes,
      ...(Array.isArray(equipment.attributes) ? equipment.attributes : []),
      ...(Array.isArray(equipment.Attributes) ? equipment.Attributes : []),
      ...attributeKeys.map((key) => equipment[key]),
    ];

    return values.some(
      (value) => value !== null && value !== undefined && String(value).trim() !== '',
    );
  }

  private hasFormEquipmentValues(): boolean {
    if (!this.equipmentItems || this.equipmentItems.length === 0) {
      return false;
    }

    const items = this.equipmentItems.getRawValue() as EquipmentFormItem[];
    return items.some((equipment) => this.hasEquipmentValue(equipment));
  }

  private normalizeEquipment(equipment: any): EquipmentFormItem {
    const equipmentTypeRaw = equipment?.equipmentType ?? equipment?.EquipmentType;
    return {
      id: this.normalizeIdValue(equipment?.id ?? equipment?.Id ?? null),
      equipmentType: this.normalizeSelectValue(equipmentTypeRaw),
      equipmentTypeLabel: this.extractEquipmentTypeLabel(equipment, equipmentTypeRaw),
      time: equipment?.time ?? equipment?.Time ?? '',
      quantity: equipment?.quantity ?? equipment?.Quantity ?? '',
      unit: equipment?.unit ?? equipment?.Unit ?? '',
      notes: equipment?.notes ?? equipment?.Notes ?? '',
      attribute1: this.normalizeSelectValue(equipment?.attribute1 ?? equipment?.Attribute1),
      attribute1Label: this.extractAttributeLabel(equipment, 1),
      attribute2: this.normalizeSelectValue(equipment?.attribute2 ?? equipment?.Attribute2),
      attribute2Label: this.extractAttributeLabel(equipment, 2),
      attribute3: this.normalizeSelectValue(equipment?.attribute3 ?? equipment?.Attribute3),
      attribute3Label: this.extractAttributeLabel(equipment, 3),
      attribute4: this.normalizeSelectValue(equipment?.attribute4 ?? equipment?.Attribute4),
      attribute4Label: this.extractAttributeLabel(equipment, 4),
      attribute5: this.normalizeSelectValue(equipment?.attribute5 ?? equipment?.Attribute5),
      attribute5Label: this.extractAttributeLabel(equipment, 5),
      attribute6: this.normalizeSelectValue(equipment?.attribute6 ?? equipment?.Attribute6),
      attribute6Label: this.extractAttributeLabel(equipment, 6),
      attribute7: this.normalizeSelectValue(equipment?.attribute7 ?? equipment?.Attribute7),
      attribute7Label: this.extractAttributeLabel(equipment, 7),
      attribute8: this.normalizeSelectValue(equipment?.attribute8 ?? equipment?.Attribute8),
      attribute8Label: this.extractAttributeLabel(equipment, 8),
      attribute9: this.normalizeSelectValue(equipment?.attribute9 ?? equipment?.Attribute9),
      attribute9Label: this.extractAttributeLabel(equipment, 9),
      attribute10: this.normalizeSelectValue(equipment?.attribute10 ?? equipment?.Attribute10),
      attribute10Label: this.extractAttributeLabel(equipment, 10),
    };
  }

  private normalizeEquipmentFromMetadata(equipment: any): EquipmentFormItem {
    const attributes =
      (Array.isArray(equipment?.attributes) && equipment.attributes) ||
      (Array.isArray(equipment?.Attributes) && equipment.Attributes) ||
      [];

    const equipmentTypeRaw =
      equipment?.equipment_type ?? equipment?.equipmentType ?? equipment?.EquipmentType;
    return {
      id: this.normalizeIdValue(
        equipment?.id ?? equipment?.Id ?? equipment?.equipment_id ?? equipment?.equipmentId ?? null,
      ),
      equipmentType: this.normalizeSelectValue(equipmentTypeRaw),
      equipmentTypeLabel: this.extractEquipmentTypeLabel(equipment, equipmentTypeRaw),
      time: equipment?.time ?? equipment?.Time ?? '',
      quantity: equipment?.quantity ?? equipment?.Quantity ?? '',
      unit: equipment?.unit ?? equipment?.Unit ?? '',
      notes: equipment?.notes ?? equipment?.Notes ?? '',
      attribute1: this.normalizeSelectValue(
        equipment?.attribute1 ?? equipment?.Attribute1 ?? attributes[0],
      ),
      attribute1Label: this.extractAttributeLabel(equipment, 1),
      attribute2: this.normalizeSelectValue(
        equipment?.attribute2 ?? equipment?.Attribute2 ?? attributes[1],
      ),
      attribute2Label: this.extractAttributeLabel(equipment, 2),
      attribute3: this.normalizeSelectValue(
        equipment?.attribute3 ?? equipment?.Attribute3 ?? attributes[2],
      ),
      attribute3Label: this.extractAttributeLabel(equipment, 3),
      attribute4: this.normalizeSelectValue(
        equipment?.attribute4 ?? equipment?.Attribute4 ?? attributes[3],
      ),
      attribute4Label: this.extractAttributeLabel(equipment, 4),
      attribute5: this.normalizeSelectValue(
        equipment?.attribute5 ?? equipment?.Attribute5 ?? attributes[4],
      ),
      attribute5Label: this.extractAttributeLabel(equipment, 5),
      attribute6: this.normalizeSelectValue(
        equipment?.attribute6 ?? equipment?.Attribute6 ?? attributes[5],
      ),
      attribute6Label: this.extractAttributeLabel(equipment, 6),
      attribute7: this.normalizeSelectValue(
        equipment?.attribute7 ?? equipment?.Attribute7 ?? attributes[6],
      ),
      attribute7Label: this.extractAttributeLabel(equipment, 7),
      attribute8: this.normalizeSelectValue(
        equipment?.attribute8 ?? equipment?.Attribute8 ?? attributes[7],
      ),
      attribute8Label: this.extractAttributeLabel(equipment, 8),
      attribute9: this.normalizeSelectValue(
        equipment?.attribute9 ?? equipment?.Attribute9 ?? attributes[8],
      ),
      attribute9Label: this.extractAttributeLabel(equipment, 9),
      attribute10: this.normalizeSelectValue(
        equipment?.attribute10 ?? equipment?.Attribute10 ?? attributes[9],
      ),
      attribute10Label: this.extractAttributeLabel(equipment, 10),
    };
  }

  private createEmptyEquipment(): EquipmentFormItem {
    return {
      id: null,
      equipmentType: null,
      time: '',
      quantity: '',
      unit: '',
      notes: '',
      attribute1: null,
      attribute2: null,
      attribute3: null,
      attribute4: null,
      attribute5: null,
      attribute6: null,
      attribute7: null,
      attribute8: null,
      attribute9: null,
      attribute10: null,
    };
  }

  private createEquipmentGroup(item?: EquipmentFormItem): FormGroup {
    const value = item ?? this.createEmptyEquipment();
    const group = this.fb.group({
      id: [value.id ?? null],
      equipmentType: [value.equipmentType ?? null],
      time: [value.time, [this.numericInputValidator()]],
      quantity: [value.quantity, [this.numericInputValidator()]],
      unit: [value.unit, [this.numericInputValidator()]],
      notes: [value.notes],
      attribute1: [value.attribute1 ?? null],
      attribute2: [value.attribute2 ?? null],
      attribute3: [value.attribute3 ?? null],
      attribute4: [value.attribute4 ?? null],
      attribute5: [value.attribute5 ?? null],
      attribute6: [value.attribute6 ?? null],
      attribute7: [value.attribute7 ?? null],
      attribute8: [value.attribute8 ?? null],
      attribute9: [value.attribute9 ?? null],
      attribute10: [value.attribute10 ?? null],
    });

    this.updateAttributeControlsState(group);

    const teardown$ = new Subject<void>();
    this.equipmentGroupSubscriptions.set(group, teardown$);

    group
      .get('equipmentType')
      ?.valueChanges.pipe(startWith(value.equipmentType ?? null), pairwise(), takeUntil(teardown$))
      .subscribe(([previous, next]) => {
        if (String(previous ?? '') === String(next ?? '')) {
          return;
        }
        this.resetAttributeControls(group);
        this.updateAttributeControlsState(group);
      });

    return group;
  }

  private setFormItems(items: EquipmentFormItem[]) {
    this.equipmentItems?.controls.forEach((control) => {
      this.teardownEquipmentGroup(control);
    });

    const nextItems = items.length > 0 ? items : [this.createEmptyEquipment()];
    this.ensureEquipmentTypeOptions(nextItems);
    this.ensureAttributeOptions(nextItems);

    const controls = nextItems.map((item) => this.createEquipmentGroup(item));
    const formArray = this.fb.array(controls);

    this.suppressPayloadUpdate = true;
    this.equipmentForm.setControl('items', formArray);
    this.equipmentForm.updateValueAndValidity({ emitEvent: false });
    this.suppressPayloadUpdate = false;
  }

  addEquipment() {
    if (this.isInstanceDesign) {
      return;
    }

    if (this.isWidgetHidden) {
      this.showWidget();
    }
    this.equipmentItems.push(this.createEquipmentGroup());
    this.updatePayload();

    this.cd.markForCheck();
  }

  public applySuggestionSet(patch: EquipmentSuggestionPatch): void {
    if (this.isInstanceDesign || !patch || typeof patch !== 'object') {
      return;
    }

    if (this.isWidgetHidden) {
      this.showWidget();
    }

    const targetIndex = this.ensureSuggestionTargetIndex();
    const target = this.equipmentItems.at(targetIndex);
    if (!(target instanceof FormGroup)) {
      return;
    }

    this.suppressPayloadUpdate = true;

    if (patch.equipmentType !== undefined) {
      target.get('equipmentType')?.setValue(this.normalizeSelectValue(patch.equipmentType), {
        emitEvent: false,
      });
      this.resetAttributeControls(target);
      this.updateAttributeControlsState(target);
    }

    this.applySuggestionValue(target, 'time', patch.time);
    this.applySuggestionValue(target, 'quantity', patch.quantity);
    this.applySuggestionValue(target, 'unit', patch.unit);
    this.applySuggestionValue(target, 'notes', patch.notes);

    this.attributeIndexes.forEach((index) => {
      const key = `attribute${index}` as keyof EquipmentSuggestionPatch;
      this.applySuggestionValue(target, String(key), patch[key]);
    });

    this.suppressPayloadUpdate = false;
    this.updatePayload();
    this.cd.markForCheck();
  }

  public getEquipmentMarkerTarget(index: number): ScribeSuggestionMarkerTarget | null {
    const rows = this.payload?.properties?.items?.value;
    const target = Array.isArray(rows) ? rows[index] : null;
    return target && typeof target === 'object' ? target : null;
  }

  public clearEquipmentMarker(index: number, fieldKey: string): void {
    const target = this.getEquipmentMarkerTarget(index);
    this.clearScribeSuggestionMarker(target, fieldKey);

    if (fieldKey === 'equipmentType') {
      this.attributeIndexes.forEach((attributeIndex) =>
        this.clearScribeSuggestionMarker(target, `attribute${attributeIndex}`),
      );
    }
  }

  removeEquipment(target: number | AbstractControl) {
    if (this.isInstanceDesign) {
      return;
    }

    const index =
      typeof target === 'number' ? target : this.equipmentItems.controls.indexOf(target);
    if (index < 0) {
      return;
    }

    if (this.equipmentItems.length <= 1) {
      this.hideWidget();
      return;
    }

    const currentRows = Array.isArray(this.payload?.properties?.items?.value)
      ? this.payload.properties.items.value
      : [];
    const remainingMarkerSources = currentRows.filter(
      (_row: unknown, rowIndex: number) => rowIndex !== index,
    );

    this.teardownEquipmentGroup(this.equipmentItems.at(index));
    this.equipmentItems.removeAt(index, { emitEvent: false });
    this.updatePayload(remainingMarkerSources);

    this.cd.markForCheck();
  }

  public getEquipmentTypeOptionsSnapshot(): Array<{ text: string; value: string }> {
    return this.allEquipmentTypes.map((option) => ({
      text: option.text,
      value: String(option.value),
    }));
  }

  public getEquipmentAttributeOptionsSnapshot(): {
    attributeIndexesByType: Array<[string, number[]]>;
    generic: Array<{ text: string; value: string }>;
    byIndex: Array<[number, Array<{ text: string; value: string }>]>;
    byEquipmentType: Array<
      [
        string,
        {
          generic: Array<{ text: string; value: string }>;
          byIndex: Array<[number, Array<{ text: string; value: string }>]>;
        },
      ]
    >;
  } {
    return {
      attributeIndexesByType: Array.from(this.equipmentAttributeIndexesByType.entries()).map(
        ([typeId, indexes]) => [typeId, [...indexes]],
      ),
      generic: this.attributeOptionsGeneric.map((option) => ({ ...option })),
      byIndex: this.attributeIndexes
        .filter((index) => (this.attributeOptionsByIndex[index] ?? []).length > 0)
        .map((index) => [
          index,
          (this.attributeOptionsByIndex[index] ?? []).map((option) => ({ ...option })),
        ]),
      byEquipmentType: Array.from(this.attributeOptionsByEquipmentType.entries()).map(
        ([typeId, store]) => [
          typeId,
          {
            generic: store.generic.map((option) => ({ ...option })),
            byIndex: this.attributeIndexes
              .filter((index) => (store.byIndex[index] ?? []).length > 0)
              .map((index) => [
                index,
                (store.byIndex[index] ?? []).map((option) => ({ ...option })),
              ]),
          },
        ],
      ),
    };
  }

  getVisibleEquipmentEntries(): Array<{ index: number; group: FormGroup }> {
    const entries = this.equipmentControls.map((group, index) => ({ index, group }));
    if (!this.isPrinting || this.isTemplatePrint) {
      return entries;
    }

    const meaningful = entries.filter((entry) => this.isEquipmentMeaningful(entry.group));
    return meaningful;
  }

  hasVisibleAttributes(group?: FormGroup): boolean {
    return this.getVisibleAttributeIndexesForRender(group).length > 0;
  }

  getVisibleAttributeIndexesForRender(group?: FormGroup): number[] {
    const indexes = this.getVisibleAttributeIndexes(group);
    if (!this.isPrinting || this.isTemplatePrint || !group) {
      return indexes;
    }

    const populatedIndexes = indexes.filter((index) => {
      const value = group.get(this.getAttributeControlName(index))?.value;
      return value !== null && value !== undefined && String(value).trim() !== '';
    });

    if (populatedIndexes.length > 0) {
      return populatedIndexes;
    }

    return indexes.length > 0 ? [indexes[0]] : [1];
  }

  onTimeBlur(group: FormGroup) {
    this.normalizeNumericControl(group.get('time'));
  }

  onQuantityBlur(group: FormGroup) {
    this.normalizeNumericControl(group.get('quantity'));
  }

  onUnitBlur(group: FormGroup) {
    this.normalizeNumericControl(group.get('unit'));
  }

  isTimeInvalid(group: FormGroup): boolean {
    const control = group.get('time');
    return this.isNumericControlInvalid(control);
  }

  isQuantityInvalid(group: FormGroup): boolean {
    const control = group.get('quantity');
    return this.isNumericControlInvalid(control);
  }

  isEquipmentTypeInvalid(group: AbstractControl | null): boolean {
    if (this.isEmrReadonly || !this.isEquipmentTypeMissing(group)) {
      return false;
    }

    const control = group?.get('equipmentType');
    return this.isSubmitted || control?.touched || control?.dirty || this.hasNonTypeValue(group);
  }

  isUnitInvalid(group: FormGroup): boolean {
    const control = group.get('unit');
    return this.isNumericControlInvalid(control);
  }

  private isNumericControlInvalid(control: AbstractControl | null): boolean {
    if (!control) {
      return false;
    }

    return control.invalid && (control.touched || control.dirty);
  }

  private normalizeNumericControl(control: AbstractControl | null) {
    if (!control) {
      return;
    }

    const raw = control.value;
    if (raw === null || raw === undefined || raw === '') {
      return;
    }

    const text = String(raw).trim();
    if (!this.isNumericLike(text)) {
      return;
    }

    const num = Number(text);
    if (!Number.isFinite(num)) {
      return;
    }

    const rounded = Math.round(num);
    const nextValue = String(rounded);
    if (nextValue !== text) {
      control.setValue(nextValue);
    }
  }

  private numericInputValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (value === null || value === undefined || value === '') {
        return null;
      }

      const text = String(value).trim();
      if (text === '') {
        return null;
      }

      return this.isNumericLike(text) ? null : { numericOnly: true };
    };
  }

  private isNumericLike(value: string): boolean {
    return /^[0-9]+(\.[0-9]+)?$/.test(value);
  }

  getAttributeOptions(index: number, group?: FormGroup): Array<{ text: string; value: string }> {
    const equipmentType = group?.get('equipmentType')?.value;
    if (equipmentType) {
      const typeId = String(equipmentType);
      const store = this.attributeOptionsByEquipmentType.get(typeId);
      if (store) {
        const byIndexOptions = store.byIndex[index] || [];
        if (byIndexOptions.length > 0) {
          return byIndexOptions;
        }
        if (store.generic.length > 0) {
          return store.generic;
        }
      }
    }

    const specific = this.attributeOptionsByIndex[index] || [];
    if (specific.length > 0) {
      return specific;
    }

    return this.attributeOptionsGeneric;
  }

  private updatePayload(markerSources?: unknown[]) {
    if (this.isInstanceDesign || this.suppressPayloadUpdate) {
      return;
    }

    if (this.isWidgetHidden) {
      return;
    }

    if (this.isTemplateEntity() && (this.isPreview || this.isDesign)) {
      return;
    }

    if (!this.payload?.properties?.items) {
      if (!this.payload?.properties) {
        return;
      }
      this.payload.properties.items = { value: [] };
    }

    const items = this.equipmentItems.getRawValue() as EquipmentFormItem[];
    const currentRows =
      markerSources ??
      (Array.isArray(this.payload.properties.items.value)
        ? this.payload.properties.items.value
        : []);
    this.payload.properties.items.value = items.map((equipment, index) => {
      const group = this.equipmentControls[index];
      const equipmentTypeValue = equipment.equipmentType || '';
      const equipmentTypeLabel = this.resolveEquipmentTypeLabel(equipmentTypeValue);
      const result: Record<string, unknown> = {
        id: equipment.id ?? null,
        equipmentType: equipmentTypeValue,
        ...(equipmentTypeLabel ? { equipmentTypeLabel } : {}),
        time: equipment.time || '',
        quantity: equipment.quantity || '',
        unit: equipment.unit || '',
        notes: equipment.notes || '',
      };

      this.attributeIndexes.forEach((attrIndex) => {
        const key = `attribute${attrIndex}` as keyof EquipmentFormItem;
        const value = String(equipment[key] ?? '').trim();
        result[String(key)] = value;
        if (!value) {
          return;
        }
        const label = this.resolveAttributeLabel(attrIndex, value, group);
        if (label) {
          result[`${String(key)}Label`] = label;
        }
      });

      copyScribeSuggestionMarker(currentRows[index], result);
      return result;
    });

    this.action.emit({
      component: FbEmrEquipmentComponent,
      data: { errorName: this.getErrorName() },
      type: 'validation-error',
    });

    if (this.isEmrReadonly) {
      this.payload.properties.items.formInvalid = false;
    }
  }

  override isValid(): boolean {
    if (this.isWidgetHidden) {
      return true;
    }

    const hasItems = this.equipmentItems?.length > 0;
    if (!hasItems) {
      return false;
    }

    const hasMissingEquipmentType = this.equipmentItems.controls.some((group) =>
      this.isEquipmentTypeMissing(group),
    );
    return !hasMissingEquipmentType && !this.hasInvalidNumericInputs();
  }

  private hasInvalidNumericInputs(): boolean {
    return this.equipmentItems.controls.some((group) => {
      const time = group.get('time');
      const quantity = group.get('quantity');
      const unit = group.get('unit');
      return (time?.invalid ?? false) || (quantity?.invalid ?? false) || (unit?.invalid ?? false);
    });
  }

  getValue() {
    return this.equipmentItems.getRawValue().map((equipment: EquipmentFormItem) => ({
      id: equipment.id ?? null,
      equipmentType: equipment.equipmentType || '',
      time: equipment.time || '',
      quantity: equipment.quantity || '',
      unit: equipment.unit || '',
      notes: equipment.notes || '',
      attribute1: equipment.attribute1 || '',
      attribute2: equipment.attribute2 || '',
      attribute3: equipment.attribute3 || '',
      attribute4: equipment.attribute4 || '',
      attribute5: equipment.attribute5 || '',
      attribute6: equipment.attribute6 || '',
      attribute7: equipment.attribute7 || '',
      attribute8: equipment.attribute8 || '',
      attribute9: equipment.attribute9 || '',
      attribute10: equipment.attribute10 || '',
    }));
  }

  private normalizeSelectValue(value: unknown): string | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    if (typeof value === 'object' && !Array.isArray(value)) {
      const record = value as Record<string, unknown>;
      const candidate =
        record['value'] ??
        record['id'] ??
        record['Value'] ??
        record['Id'] ??
        record['code'] ??
        record['Code'] ??
        null;
      if (candidate !== null && candidate !== undefined && String(candidate).trim() !== '') {
        return String(candidate);
      }
      return null;
    }

    return String(value);
  }

  private normalizeLabelValue(value: unknown): string | null {
    if (value === null || value === undefined) {
      return null;
    }

    const text = String(value).trim();
    return text ? text : null;
  }

  private extractEquipmentTypeLabel(item: any, raw: unknown): string | null {
    return (
      this.normalizeLabelValue(
        item?.equipmentTypeLabel ??
          item?.equipment_type_label ??
          item?.equipmentTypeName ??
          item?.EquipmentTypeName ??
          item?.equipment_name ??
          item?.EquipmentName,
      ) ?? this.extractSelectLabel(raw)
    );
  }

  private extractAttributeLabel(item: any, index: number): string | null {
    return this.normalizeLabelValue(
      item?.[`attribute${index}Label`] ??
        item?.[`Attribute${index}Label`] ??
        item?.[`attribute_${index}_label`] ??
        item?.[`attribute${index}Name`] ??
        item?.[`Attribute${index}Name`],
    );
  }

  private extractSelectLabel(raw: unknown): string | null {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
      return null;
    }

    const record = raw as Record<string, unknown>;
    return this.normalizeLabelValue(
      record['text'] ??
        record['name'] ??
        record['label'] ??
        record['Text'] ??
        record['Name'] ??
        record['Label'] ??
        null,
    );
  }

  private normalizeIdValue(value: unknown): string | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    return String(value);
  }

  private isTemplateEntity(): boolean {
    const entity = this.entity as any;
    if (!entity) {
      return true;
    }

    if ('patient' in entity) {
      return !entity.patient;
    }

    return true;
  }

  public getErrorName(): string | null {
    if (this.isWidgetHidden || this.isEmrReadonly) {
      return null;
    }

    if (this.hasInvalidNumericInputs()) {
      return 'numericOnly';
    }

    const hasItems = this.equipmentItems?.length > 0;
    const hasMissingEquipmentType = this.equipmentItems.controls.some((group) =>
      this.isEquipmentTypeMissing(group),
    );
    if (!hasItems || hasMissingEquipmentType) {
      return 'required';
    }

    return null;
  }

  private resetAttributeControls(group: FormGroup): void {
    this.attributeIndexes.forEach((index) => {
      group.get(this.getAttributeControlName(index))?.setValue(null);
    });
  }

  private updateAttributeControlsState(group: FormGroup): void {
    const value = group.get('equipmentType')?.value;
    const hasType = value !== null && value !== undefined && String(value).trim() !== '';
    const isReadOnly = this.isDesign || this.isPrinting || this.isEmrReadonly;
    const shouldDisable = isReadOnly || !hasType;
    const shouldClear = !hasType && !isReadOnly;
    this.attributeIndexes.forEach((index) => {
      const control = group.get(this.getAttributeControlName(index));
      if (!control) {
        return;
      }
      if (shouldDisable) {
        if (shouldClear) {
          control.setValue(null, { emitEvent: false });
        }
        if (control.enabled) {
          control.disable({ emitEvent: false });
        }
      } else if (control.disabled) {
        control.enable({ emitEvent: false });
      }
    });
  }

  private teardownEquipmentGroup(control: AbstractControl | null): void {
    if (!control || !(control instanceof FormGroup)) {
      return;
    }

    const teardown$ = this.equipmentGroupSubscriptions.get(control);
    if (teardown$) {
      teardown$.next();
      teardown$.complete();
      this.equipmentGroupSubscriptions.delete(control);
    }
  }

  private isEquipmentTypeMissing(group: AbstractControl | null): boolean {
    const value = group?.get('equipmentType')?.value;
    return value === null || value === undefined || String(value).trim() === '';
  }

  private hasNonTypeValue(group: AbstractControl | null): boolean {
    if (!group || !(group instanceof FormGroup)) {
      return false;
    }

    const value = group.getRawValue() as EquipmentFormItem;
    const fields = [
      value.time,
      value.quantity,
      value.unit,
      value.notes,
      value.attribute1,
      value.attribute2,
      value.attribute3,
      value.attribute4,
      value.attribute5,
      value.attribute6,
      value.attribute7,
      value.attribute8,
      value.attribute9,
      value.attribute10,
    ];

    return fields.some(
      (field) => field !== null && field !== undefined && String(field).trim() !== '',
    );
  }

  private ensureEquipmentTypeOptions(items: EquipmentFormItem[]) {
    const existingValues = new Set(this.allEquipmentTypes.map((option) => String(option.value)));
    const additions: Array<{ text: string; value: string }> = [];

    items.forEach((item) => {
      const value = item?.equipmentType;
      if (!value) {
        return;
      }
      const normalized = String(value);
      if (!existingValues.has(normalized)) {
        existingValues.add(normalized);
        additions.push({
          text: this.normalizeLabelValue(item?.equipmentTypeLabel) ?? normalized,
          value: normalized,
        });
      }
    });

    if (additions.length === 0) {
      return;
    }

    this.allEquipmentTypes = this.mergeEquipmentOptions([...additions, ...this.allEquipmentTypes]);
    this.equipmentTypes$.next(this.allEquipmentTypes);
  }

  private ensureAttributeOptions(items: EquipmentFormItem[]) {
    this.attributeIndexes.forEach((index) => {
      const key = `attribute${index}` as keyof EquipmentFormItem;
      const labelKey = `attribute${index}Label` as keyof EquipmentFormItem;
      items.forEach((item) => {
        const value = item?.[key];
        if (!value) {
          return;
        }
        this.addAttributeFallbackOption(
          index,
          String(value),
          item?.equipmentType ?? null,
          this.normalizeLabelValue(item?.[labelKey]),
        );
      });
    });
  }

  private resolveEquipmentTypeLabel(value: string): string | null {
    if (!value) {
      return null;
    }

    const option = this.allEquipmentTypes.find((item) => String(item.value) === String(value));
    return this.normalizeLabelValue(option?.text) ?? this.normalizeLabelValue(value);
  }

  private resolveAttributeLabel(index: number, value: string, group?: FormGroup): string | null {
    if (!value) {
      return null;
    }

    const option = this.getAttributeOptions(index, group).find(
      (item) => String(item.value) === String(value),
    );
    return this.normalizeLabelValue(option?.text) ?? this.normalizeLabelValue(value);
  }

  private getMetadataEquipments(): any[] {
    const metadata = this.entity?.metadata;
    const items = metadata?.equipments ?? metadata?.equipment ?? null;
    return Array.isArray(items) ? items : [];
  }

  private getSelectedEquipmentTypeValues(): Set<string> {
    if (!this.equipmentItems || this.equipmentItems.length === 0) {
      return new Set();
    }

    const values = (this.equipmentItems.getRawValue() as EquipmentFormItem[])
      .map((item) => item?.equipmentType)
      .filter((value) => value !== null && value !== undefined && value !== '')
      .map((value) => String(value));

    return new Set(values);
  }

  private mergeEquipmentOptions(
    options: Array<{ text: string; value: string }>,
  ): Array<{ text: string; value: string }> {
    const seen = new Map<string, number>();
    const merged: Array<{ text: string; value: string }> = [];

    options.forEach((option) => {
      const normalized = String(option.value);
      const existingIndex = seen.get(normalized);
      const candidate = { text: option.text, value: normalized };

      if (existingIndex === undefined) {
        seen.set(normalized, merged.length);
        merged.push(candidate);
        return;
      }

      const existing = merged[existingIndex];
      const existingIsFallback = existing.text === existing.value;
      const candidateIsFallback = candidate.text === candidate.value;

      if (existingIsFallback && !candidateIsFallback) {
        merged[existingIndex] = candidate;
      }
    });

    return merged;
  }

  private mergeAttributeOptions(
    options: Array<{ text: string; value: string }>,
  ): Array<{ text: string; value: string }> {
    const seen = new Map<string, number>();
    const merged: Array<{ text: string; value: string }> = [];

    options.forEach((option) => {
      const normalized = String(option.value);
      const existingIndex = seen.get(normalized);
      const candidate = { text: option.text, value: normalized };

      if (existingIndex === undefined) {
        seen.set(normalized, merged.length);
        merged.push(candidate);
        return;
      }

      const existing = merged[existingIndex];
      const existingIsFallback = existing.text === existing.value;
      const candidateIsFallback = candidate.text === candidate.value;

      if (existingIsFallback && !candidateIsFallback) {
        merged[existingIndex] = candidate;
      }
    });

    return merged;
  }

  private getTotalPages(headers: any, pageSize: number, fallbackCount: number): number | null {
    if (!headers?.get) {
      return fallbackCount === pageSize ? null : 0;
    }

    const direct = this.getHeaderNumber(headers, [
      'X-Total-Pages',
      'X-Total-Page',
      'X-Pages',
      'X-Page-Count',
      'DataTotalPages',
      'datatotalpages',
    ]);
    if (direct) {
      return direct;
    }

    const totalCount = this.getHeaderNumber(headers, [
      'X-Total-Count',
      'Total-Count',
      'DataTotalRows',
      'datatotalrows',
    ]);
    if (totalCount) {
      return Math.ceil(totalCount / pageSize);
    }

    const pagination = headers.get('X-Pagination') || headers.get('Pagination');
    if (pagination) {
      try {
        const parsed = JSON.parse(pagination);
        const total = Number(parsed?.totalPages ?? parsed?.TotalPages);
        if (!Number.isNaN(total) && total > 0) {
          return total;
        }
      } catch {
        return fallbackCount === pageSize ? null : 0;
      }
    }

    return fallbackCount === pageSize ? null : 0;
  }

  private getHeaderNumber(headers: any, names: string[]): number | null {
    for (const name of names) {
      const raw = headers.get(name);
      if (!raw) {
        continue;
      }

      const value = Number(raw);
      if (!Number.isNaN(value) && value > 0) {
        return value;
      }
    }

    return null;
  }

  private updateEquipmentAttributeConfig(item: any, value: string | number) {
    if (!item) {
      return;
    }

    const equipmentTypeId = String(value);
    const indexes = this.resolveAttributeIndexes(item);
    if (indexes.length === 0) {
      return;
    }

    this.equipmentAttributeIndexesByType.set(equipmentTypeId, indexes);
  }

  private updateAttributeIndexesByType() {
    const indexesByType = new Map<string, number[]>();
    this.allAttributeOptions.forEach((option) => {
      if (!option.equipmentTypeId || option.index === null) {
        return;
      }
      const typeId = String(option.equipmentTypeId);
      if (!indexesByType.has(typeId)) {
        indexesByType.set(typeId, []);
      }
      indexesByType.get(typeId)?.push(option.index);
    });

    indexesByType.forEach((indexes, typeId) => {
      const unique = Array.from(new Set(indexes)).sort((a, b) => a - b);
      if (unique.length > 0) {
        this.equipmentAttributeIndexesByType.set(typeId, unique);
      }
    });
  }

  private resolveAttributeIndexes(item: any): number[] {
    const explicitIndexes: number[] = [];
    const attributesArray = Array.isArray(item?.Attributes)
      ? item.Attributes
      : Array.isArray(item?.attributes)
        ? item.attributes
        : [];

    if (attributesArray.length > 0) {
      attributesArray.forEach((entry) => {
        const rawIndex =
          entry?.AttributeNo ??
          entry?.AttributeIndex ??
          entry?.AttributeNumber ??
          entry?.AttributePosition ??
          entry?.AttributeOrder ??
          null;
        if (rawIndex === null || rawIndex === undefined) {
          return;
        }
        const normalized = Number(rawIndex);
        if (!Number.isNaN(normalized) && normalized >= 1 && normalized <= 10) {
          explicitIndexes.push(normalized);
        }
      });
    }

    if (explicitIndexes.length > 0) {
      return Array.from(new Set(explicitIndexes)).sort((a, b) => a - b);
    }

    const attributeIds = Array.isArray(item?.AttributeIds)
      ? item.AttributeIds
      : Array.isArray(item?.attributeIds)
        ? item.attributeIds
        : [];

    if (attributeIds.length > 0) {
      return attributeIds
        .map((rawIndex: any) => Number(rawIndex))
        .filter(
          (normalized: number) => !Number.isNaN(normalized) && normalized >= 1 && normalized <= 10,
        )
        .sort((a, b) => a - b);
    }

    const configuredIndexes = this.attributeIndexes.filter((index) => {
      const idValue =
        item[`AttributeId${index}`] ??
        item[`Attribute${index}`] ??
        item[`attribute${index}`] ??
        null;
      return idValue !== null && idValue !== undefined && idValue !== '';
    });
    if (configuredIndexes.length > 0) {
      return configuredIndexes;
    }

    const countCandidates = [
      item?.AttributeCount,
      item?.AttributesCount,
      item?.AttributeQty,
      item?.AttributeTotal,
      attributesArray.length,
    ].filter((candidate: any) => candidate !== null && candidate !== undefined && candidate !== '');

    const count = countCandidates.length > 0 ? Number(countCandidates[0]) : 0;
    if (!Number.isNaN(count) && count > 0) {
      return this.attributeIndexes.slice(0, Math.min(count, this.attributeIndexes.length));
    }

    return [];
  }

  private addAttributeFallbackOption(
    index: number,
    value: string,
    equipmentType: string | null,
    text?: string | null,
  ) {
    const normalized = String(value);
    const normalizedText = this.normalizeLabelValue(text) ?? normalized;
    const existingOptions = this.attributeOptionsByIndex[index] || [];
    const existingValues = new Set(existingOptions.map((option) => String(option.value)));
    if (!existingValues.has(normalized)) {
      this.attributeOptionsByIndex[index] = this.mergeAttributeOptions([
        { text: normalizedText, value: normalized },
        ...existingOptions,
      ]);
    }

    if (equipmentType) {
      const typeId = String(equipmentType);
      const store = this.attributeOptionsByEquipmentType.get(typeId) || {
        generic: [],
        byIndex: {},
      };
      const indexOptions = store.byIndex[index] || [];
      const indexValues = new Set(indexOptions.map((option) => String(option.value)));
      if (!indexValues.has(normalized)) {
        store.byIndex[index] = this.mergeAttributeOptions([
          { text: normalizedText, value: normalized },
          ...indexOptions,
        ]);
        this.attributeOptionsByEquipmentType.set(typeId, store);
      }
    }
  }

  getVisibleAttributeIndexes(group?: FormGroup): number[] {
    const equipmentType = group?.get('equipmentType')?.value;
    if (!equipmentType) {
      return this.attributeIndexes;
    }

    const typeId = String(equipmentType);
    const indexes = this.equipmentAttributeIndexesByType.get(typeId);
    if (indexes && indexes.length > 0) {
      return indexes;
    }

    return this.attributeIndexes;
  }

  private isEquipmentMeaningful(group: FormGroup): boolean {
    return this.hasEquipmentValue(group.getRawValue());
  }

  private parseAttributeOption(
    item: any,
  ): { text: string; value: string; index: number | null; equipmentTypeId: string | null } | null {
    if (!item || typeof item !== 'object') {
      return null;
    }

    const text =
      item.Name || item.AttributeName || item.Description || item.Text || item.Label || '';

    const value =
      item.Id ??
      item.ValueId ??
      item.AttributeValueId ??
      item.AttributeOptionId ??
      item.OptionId ??
      item.Code ??
      item.Value ??
      item.Attribute ??
      item.id ??
      null;

    if (value === null || value === undefined || value === '') {
      return null;
    }

    let index: number | null = null;
    const explicitIndex =
      item.AttributeGroup ??
      item.AttributeNo ??
      item.AttributeIndex ??
      item.AttributeNumber ??
      item.AttributePosition ??
      item.AttributeOrder ??
      null;

    if (explicitIndex !== null && explicitIndex !== undefined) {
      const normalizedIndex = Number(explicitIndex);
      if (!Number.isNaN(normalizedIndex) && normalizedIndex >= 1 && normalizedIndex <= 10) {
        index = normalizedIndex;
      }
    }

    if (index === null) {
      const attributeId = item.AttributeId;
      if (attributeId !== null && attributeId !== undefined) {
        const normalizedAttributeId = Number(attributeId);
        if (
          !Number.isNaN(normalizedAttributeId) &&
          normalizedAttributeId >= 1 &&
          normalizedAttributeId <= 10 &&
          String(value) !== String(attributeId)
        ) {
          index = normalizedAttributeId;
        }
      }
    }

    const equipmentTypeId =
      item.TreatmentEquipmentId ??
      item.TreatmentEquipmentItemId ??
      item.TreatmentEquipmentTypeId ??
      item.EquipmentTypeId ??
      item.EquipmentId ??
      item.EquipmentType ??
      item.treatmentEquipmentId ??
      item.equipmentTypeId ??
      item.equipmentId ??
      item.equipmentType ??
      null;

    return {
      text: text || String(value),
      value: String(value),
      index,
      equipmentTypeId:
        equipmentTypeId === null || equipmentTypeId === undefined || equipmentTypeId === ''
          ? null
          : String(equipmentTypeId),
    };
  }

  public getMetadata(): Record<string, unknown> {
    if (this.isWidgetHidden) {
      return {};
    }

    if (this.payload && this.payload.properties) {
      const widgetData = {
        ...this.payload,
        properties: { ...this.payload.properties },
      };
      const widget = new FbEmrEquipmentWidget(widgetData);
      return widget.getMetadata();
    }

    return {};
  }

  private hideWidget(): void {
    if (this.isInstanceDesign || this.isWidgetHidden) {
      return;
    }

    this.isWidgetHidden = true;

    if (this.payload?.properties?.items) {
      this.payload.properties.items.value = [];
      this.payload.properties.items.hidden = true;
    }

    this.suppressPayloadUpdate = true;
    if (this.equipmentItems && this.equipmentItems.length > 0) {
      this.equipmentItems.controls.forEach((control) => {
        this.teardownEquipmentGroup(control as FormGroup);
      });
    }
    this.equipmentGroupSubscriptions.clear();
    this.equipmentItems.clear();
    this.equipmentForm.updateValueAndValidity({ emitEvent: false });
    this.suppressPayloadUpdate = false;
    this.cd.markForCheck();
  }

  private showWidget(): void {
    if (this.isInstanceDesign || !this.isWidgetHidden) {
      return;
    }

    this.isWidgetHidden = false;

    if (this.payload?.properties?.items) {
      this.payload.properties.items.hidden = false;
      if (!Array.isArray(this.payload.properties.items.value)) {
        this.payload.properties.items.value = [];
      }
    }
  }

  private ensureSuggestionTargetIndex(): number {
    const emptyIndex = this.equipmentControls.findIndex(
      (group) => !this.isEquipmentMeaningful(group),
    );
    if (emptyIndex >= 0) {
      return emptyIndex;
    }

    this.equipmentItems.push(this.createEquipmentGroup());
    return this.equipmentItems.length - 1;
  }

  private applySuggestionValue(group: FormGroup, controlName: string, rawValue: unknown): void {
    if (rawValue === undefined) {
      return;
    }

    const control = group.get(controlName);
    if (!control) {
      return;
    }

    if (controlName === 'time' || controlName === 'quantity' || controlName === 'unit') {
      const nextValue =
        rawValue === null || rawValue === undefined || `${rawValue}`.trim() === ''
          ? ''
          : String(rawValue).trim();
      control.setValue(nextValue, { emitEvent: false });
      return;
    }

    if (controlName === 'notes') {
      control.setValue(rawValue == null ? '' : String(rawValue), { emitEvent: false });
      return;
    }

    control.setValue(this.normalizeSelectValue(rawValue), { emitEvent: false });
  }
}
